// WeeklyPassengersChart component - Bar chart for passengers by day of week with year comparison

import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { formatNumber, getWeekdayName } from "../../utils/formatters";
import { groupByDayOfWeek } from "../../utils/calculations";
import { WEEKLY_CHART_COLORS, YEAR_COLORS } from "../../constants";
import { getDayOfWeekChart } from "../../services/api";

const WeeklyPassengersChart = ({ data, filters }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);

  // Toggle states for data types
  const [showRegular, setShowRegular] = useState(true);
  const [showDaypass, setShowDaypass] = useState(true);

  // Days of week mapping
  const daysOfWeek = [
    { name: "จันทร์", index: 1 },
    { name: "อังคาร", index: 2 },
    { name: "พุธ", index: 3 },
    { name: "พฤหัสบดี", index: 4 },
    { name: "ศุกร์", index: 5 },
    { name: "เสาร์", index: 6 },
    { name: "อาทิตย์", index: 0 },
  ];

  // Fetch day of week data from API
  useEffect(() => {
    const fetchDayOfWeekData = async () => {
      setLoading(true);
      try {
        // Only use startDate and endDate if explicitly selected from filter
        const effectiveStartDate = filters?.startDate || null;
        const effectiveEndDate = filters?.endDate || null;

        const result = await getDayOfWeekChart(
          effectiveStartDate,
          effectiveEndDate,
          filters?.busLine,
          filters?.selectedYears && filters.selectedYears.length > 0
            ? filters.selectedYears.join(",")
            : null,
        );

        if (result.success && result.data) {
          const apiData = result.data;
          const processedData = [];

          // Check if data is array (flat structure) or object (year-grouped)
          if (Array.isArray(apiData)) {
            // Flat array structure: [{ day_number, day_name, passengers, ... }]
            apiData.forEach((dayData) => {
              const passengers = dayData.passengers || 0;
              const daypass = dayData.daypass || 0;
              const regularPassengers = passengers - daypass;

              processedData.push({
                day: dayData.day_name || getWeekdayName(dayData.day_number),
                dayIndex: dayData.day_number,
                passengers: regularPassengers, // ตั๋วฉีกอย่างเดียว
                daypass: daypass,
                total: passengers, // รวมทั้งหมด
                rawPassengers: dayData.passengers || 0,
                dayCount: dayData.day_count || 1,
                year: new Date().getFullYear(),
                color: WEEKLY_CHART_COLORS[dayData.day_number] || "#3B82F6",
              });
            });
          } else {
            // Year-grouped structure: { "2024": [...], "2025": [...] }
            Object.keys(apiData).forEach((year) => {
              const yearData = apiData[year];
              yearData.forEach((dayData) => {
                const passengers = dayData.passengers || 0;
                const daypass = dayData.daypass || 0;
                const regularPassengers = passengers - daypass;

                processedData.push({
                  day: dayData.day_name || getWeekdayName(dayData.day_number),
                  dayIndex: dayData.day_number,
                  passengers: regularPassengers, // ตั๋วฉีกอย่างเดียว
                  daypass: daypass,
                  total: passengers, // รวมทั้งหมด
                  rawPassengers: dayData.passengers || 0,
                  dayCount: dayData.day_count || 1,
                  year: parseInt(year, 10),
                  color: WEEKLY_CHART_COLORS[dayData.day_number] || "#3B82F6",
                });
              });
            });
          }

          // console.log("Processed weekday data:", processedData);
          setChartData(processedData);
        } else {
          // Fallback to local calculation
          const weeklyData = groupByDayOfWeek(data);
          const localChartData = [];
          for (let i = 1; i <= 7; i++) {
            const dayIndex = i === 7 ? 0 : i;
            const dayData = weeklyData[dayIndex];
            const average =
              dayData.count > 0 ? Math.round(dayData.total / dayData.count) : 0;

            localChartData.push({
              day: getWeekdayName(dayIndex),
              dayIndex: dayIndex,
              passengers: average,
              year: new Date().getFullYear(),
              color: WEEKLY_CHART_COLORS[dayIndex],
            });
          }
          setChartData(localChartData);
        }
      } catch (error) {
        console.error("Error fetching day of week data:", error);
        // Fallback to local calculation
        const weeklyData = groupByDayOfWeek(data);
        const localChartData = [];
        for (let i = 1; i <= 7; i++) {
          const dayIndex = i === 7 ? 0 : i;
          const dayData = weeklyData[dayIndex];
          const average =
            dayData.count > 0 ? Math.round(dayData.total / dayData.count) : 0;

          localChartData.push({
            day: getWeekdayName(dayIndex),
            dayIndex: dayIndex,
            passengers: average,
            year: new Date().getFullYear(),
            color: WEEKLY_CHART_COLORS[dayIndex],
          });
        }
        setChartData(localChartData);
      } finally {
        setLoading(false);
      }
    };

    if (data && data.length > 0) {
      fetchDayOfWeekData();
    }
  }, [data, filters]);

  // Get available years from chartData
  useEffect(() => {
    if (chartData && chartData.length > 0) {
      const years = [
        ...new Set(
          chartData
            .map((item) => item.year)
            .filter(
              (year) => year && !isNaN(year) && year > 2000 && year < 2100,
            ),
        ),
      ].sort((a, b) => b - a);

      // console.log("Available years from API (Weekday):", years);

      setAvailableYears(years);

      // Select last 2 years by default (only on first load)
      if (years.length > 0 && selectedYears.length === 0) {
        setSelectedYears(years.slice(0, Math.min(2, years.length)));
      }
    }
  }, [chartData]);

  // Process comparison data when selected years change
  useEffect(() => {
    if (chartData.length > 0 && selectedYears.length > 0) {
      // Group data by day of week for comparison
      const grouped = {};

      daysOfWeek.forEach((dow) => {
        grouped[dow.index] = {
          day: dow.name,
          dayIndex: dow.index,
        };
      });

      chartData.forEach((item) => {
        const dayIndex = item.dayIndex;
        const year = item.year;

        if (selectedYears.includes(year) && grouped[dayIndex]) {
          grouped[dayIndex][`passengers_${year}`] = item.passengers;
          grouped[dayIndex][`daypass_${year}`] = item.daypass;
          grouped[dayIndex][`total_${year}`] = item.total;
        }
      });

      const compData = Object.values(grouped).sort((a, b) => {
        const aIndex = a.dayIndex === 0 ? 7 : a.dayIndex;
        const bIndex = b.dayIndex === 0 ? 7 : b.dayIndex;
        return aIndex - bIndex;
      });

      setComparisonData(compData);
    }
  }, [chartData, selectedYears]);

  // Toggle year selection
  const toggleYear = (year) => {
    if (selectedYears.includes(year)) {
      setSelectedYears(selectedYears.filter((y) => y !== year));
    } else {
      setSelectedYears([...selectedYears, year].sort((a, b) => b - a));
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border-2 border-gray-300 rounded-xl shadow-2xl">
          <p className="text-sm font-bold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => {
            let typeLabel = "";
            if (showRegular && showDaypass) {
              typeLabel = "รวมทั้งหมด";
            } else if (showRegular && !showDaypass) {
              typeLabel = "ตั๋วฉีก";
            } else if (!showRegular && showDaypass) {
              typeLabel = "Daypass";
            }

            return (
              <div key={index} className="flex items-center gap-2 mb-1">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                ></div>
                <p
                  className="text-sm font-semibold"
                  style={{ color: entry.color }}
                >
                  ปี {entry.name} ({typeLabel}): {formatNumber(entry.value)} คน
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-xl p-6 border-2 border-purple-200 transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-purple-800">
                กราฟจำนวนผู้โดยสารตามวันในสัปดาห์
              </h3>
              <p className="text-sm text-purple-600">
                {!filters?.startDate && !filters?.endDate
                  ? "แสดงข้อมูล 2 ปีย้อนหลัง"
                  : "แสดงตามช่วงเวลาที่เลือก"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center h-80 text-purple-400">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
            <p className="text-sm font-medium">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </div>
    );
  }

  if (chartData.length === 0 || chartData.every((d) => d.passengers === 0)) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-xl p-6 border-2 border-purple-200 transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-purple-800">
                กราฟจำนวนผู้โดยสารตามวันในสัปดาห์
              </h3>
              <p className="text-sm text-purple-600">
                {!filters?.startDate && !filters?.endDate
                  ? "แสดงข้อมูล 2 ปีย้อนหลัง"
                  : "แสดงตามช่วงเวลาที่เลือก"}
              </p>
            </div>

            {/* Toggle Buttons for Data Types */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowRegular(!showRegular)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                  showRegular
                    ? "text-white shadow-lg"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
                style={
                  showRegular
                    ? {
                        background:
                          showRegular && showDaypass
                            ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                            : "#3b82f6",
                      }
                    : {}
                }
              >
                {showRegular ? "✓" : ""} ตั๋วฉีก
              </button>
              <button
                onClick={() => setShowDaypass(!showDaypass)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                  showDaypass
                    ? "text-white shadow-lg"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
                style={
                  showDaypass
                    ? {
                        background:
                          showRegular && showDaypass
                            ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                            : "#a855f7",
                      }
                    : {}
                }
              >
                {showDaypass ? "✓" : ""} Daypass
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center h-80 text-gray-400">
          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-purple-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <p className="text-gray-600 font-medium">
              ไม่มีข้อมูลสำหรับแสดงกราฟ
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-xl border-2 border-purple-200 transition-all duration-300 hover:shadow-2xl overflow-hidden">
      {/* Header with Toggle Button */}
      <div className="p-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-purple-800 flex items-center gap-2">
                กราฟจำนวนผู้โดยสารตามวันในสัปดาห์
                {selectedYears.length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-bold bg-purple-200 text-purple-700 rounded-full">
                    เปรียบเทียบ {selectedYears.length} ปี
                  </span>
                )}
              </h3>
              <p className="text-sm text-purple-600 mt-0.5">
                {!filters?.startDate && !filters?.endDate
                  ? "แสดงข้อมูล 2 ปีย้อนหลัง"
                  : "แสดงตามช่วงเวลาที่เลือก"}
              </p>
            </div>
          </div>

          {/* Toggle Buttons for Data Types */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowRegular(!showRegular)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                showRegular
                  ? "text-white shadow-lg"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
              style={
                showRegular
                  ? {
                      background:
                        showRegular && showDaypass
                          ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                          : "#3b82f6",
                    }
                  : {}
              }
            >
              {showRegular ? "✓" : ""} ตั๋วฉีก
            </button>
            <button
              onClick={() => setShowDaypass(!showDaypass)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                showDaypass
                  ? "text-white shadow-lg"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
              style={
                showDaypass
                  ? {
                      background:
                        showRegular && showDaypass
                          ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                          : "#a855f7",
                    }
                  : {}
              }
            >
              {showDaypass ? "✓" : ""} Daypass
            </button>
          </div>
        </div>

        {/* Year Selection */}
        <div className="flex flex-wrap gap-2 mt-4">
          {availableYears.map((year, index) => (
            <button
              key={year}
              onClick={() => toggleYear(year)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                selectedYears.includes(year)
                  ? "text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              style={
                selectedYears.includes(year)
                  ? { background: YEAR_COLORS[index % YEAR_COLORS.length] }
                  : {}
              }
            >
              {year} {selectedYears.includes(year) && "✓"}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Content */}
      <div className="transition-all duration-500 ease-in-out overflow-hidden max-h-[600px] opacity-100">
        <div className="px-6 pb-6">
          {selectedYears.length > 0 ? (
            <>
              <div className="bg-white rounded-xl p-4 shadow-inner border border-purple-100">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={comparisonData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3E8FF" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 12, fill: "#6B7280" }}
                        stroke="#9CA3AF"
                        tickLine={{ stroke: "#E5E7EB" }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#6B7280" }}
                        stroke="#9CA3AF"
                        tickLine={{ stroke: "#E5E7EB" }}
                        tickFormatter={(value) => formatNumber(value)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{
                          paddingTop: "20px",
                        }}
                        formatter={(value) => `ปี ${value}`}
                      />
                      {selectedYears.map((year, index) => {
                        let dataKey;
                        if (showRegular && showDaypass) {
                          dataKey = `total_${year}`;
                        } else if (showRegular && !showDaypass) {
                          dataKey = `passengers_${year}`;
                        } else if (!showRegular && showDaypass) {
                          dataKey = `daypass_${year}`;
                        } else {
                          return null;
                        }

                        return (
                          <Bar
                            key={year}
                            dataKey={dataKey}
                            name={year}
                            fill={YEAR_COLORS[index % YEAR_COLORS.length]}
                            radius={[8, 8, 0, 0]}
                            animationDuration={1000}
                          />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Statistics Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                {selectedYears.map((year, index) => {
                  const yearData = chartData.filter(
                    (item) => item.year === year,
                  );

                  // Find the day with maximum raw passengers
                  const maxItem =
                    yearData.length > 0
                      ? yearData.reduce((prev, current) =>
                          current.rawPassengers > prev.rawPassengers
                            ? current
                            : prev,
                        )
                      : null;

                  // Calculate average: max day's raw passengers / day_count
                  const avg = maxItem
                    ? Math.round(maxItem.rawPassengers / maxItem.dayCount)
                    : 0;

                  const max = maxItem ? maxItem.rawPassengers : 0;
                  const maxDay = maxItem ? maxItem.day : "-";

                  return (
                    <div
                      key={year}
                      className="bg-white rounded-xl p-4 border-2 shadow-md"
                      style={{
                        borderColor: YEAR_COLORS[index % YEAR_COLORS.length],
                      }}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{
                            backgroundColor:
                              YEAR_COLORS[index % YEAR_COLORS.length],
                          }}
                        ></div>
                        <p className="text-sm font-bold text-gray-700">
                          สถิติปี {year}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-600">
                            วันที่มีมากสุด
                          </p>
                          <p className="text-sm font-semibold text-gray-700">
                            {maxDay} ({formatNumber(max)} คน)
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">
                            ค่าเฉลี่ยต่อวัน
                          </p>
                          <p
                            className="text-lg font-bold"
                            style={{
                              color: YEAR_COLORS[index % YEAR_COLORS.length],
                            }}
                          >
                            {formatNumber(Math.round(avg))} คน
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl p-8 text-center border-2 border-amber-200">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-amber-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-gray-600 font-medium">
                กรุณาเลือกปีเพื่อแสดงกราฟเปรียบเทียบ
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyPassengersChart;
